import React, { useMemo } from 'react';
import { filter, forEach, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Grid,
  Paper,
  RequiredKey,
  Typography,
  WarningTriangleIcon,
} from '@availity/element';

import { AvailityHeaderLogo, Footer, OAAAdminChangeTable } from '@/components';
import { useAppContext } from '@/context';

const OAAAdminChange = ({ ...props }) => {
  const { setCurrentOAAPage, setSkipBackPage, adminChangeFormValues, setAdminChangeFormValues, reasonOptions } = props;
  const { oaaOrgsResponse, selectedOAAOrgIds } = useAppContext();

  const { oaaOrganizations, skipCount = 0 } = oaaOrgsResponse;

  // only show the skip button if skip count is less than 3
  const showSkipButton = useMemo(() => skipCount < 3, [skipCount]);

  // extract orgs not selected - for display
  const organizationsToDisplay = useMemo(
    () => filter(oaaOrganizations, (organization) => selectedOAAOrgIds.indexOf(organization.appId) < 0),
    [selectedOAAOrgIds, oaaOrganizations]
  );

  // build out initial form state based on orgs not selected
  const initialFormState = useMemo(() => {
    const output = {};

    // iterate through all organizations displayed on the page
    for (const organization of organizationsToDisplay) {
      const { appId } = organization;
      output[appId] = {};

      // check if field values exist from existing form state
      const rowValues = adminChangeFormValues?.[appId] || {};

      // populate form values
      output[appId].reason = rowValues.reason || '';
      output[appId].admin = rowValues.admin || '';
      output[appId].comments = rowValues.comments || '';
    }

    return output;
  }, [oaaOrganizations]);

  // react-hook-form handlers
  const formHandlers = useForm({
    defaultValues: {
      ...initialFormState,
    },
  });
  const { handleSubmit, formState = {}, getValues } = formHandlers;

  // for displaying the error count if needed
  const errorCount = useMemo(() => {
    const { errors } = formState;
    let output = 0;

    if (!isEmpty(errors)) {
      // iterate through error objects in form and add up all the errors
      forEach(errors, (orgErrorGroup) => {
        const errorFields = Object.entries(orgErrorGroup);
        const numberOfErrors = errorFields.length;

        output += numberOfErrors;
      });
    }

    return output;
  }, [formState]);

  // handle skip navigation
  const handleSkip = () => {
    // need to save form state
    const values = getValues();
    if (values) {
      setAdminChangeFormValues(values);
    }

    setSkipBackPage('adminChange');
    setCurrentOAAPage('skipOAA');
  };

  // back navigation logic
  const handleBack = () => {
    // need to save form state
    const values = getValues();
    if (values) {
      setAdminChangeFormValues(values);
    }

    setCurrentOAAPage('adminCheckIn');
  };

  // submit logic
  const onSubmit = (values) => {
    setAdminChangeFormValues(values);

    setCurrentOAAPage('adminChangeReview');
  };

  return (
    <Grid
      id="oaa-admin-change-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid size={{ xs: 12, sm: 12, md: 6 }} display="flex" flexDirection="column" alignItems="center" flexGrow={1}>
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <FormProvider {...formHandlers}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Paper
                variant="elevation"
                elevation={6}
                sx={{ width: { xs: '350px', sm: '500px', md: '900px', lg: '1200px' } }}
              >
                <CardContent>
                  <Typography variant="h1" mb={2}>
                    Primary Administrator Updates Requested
                  </Typography>
                  <Typography variant="subtitle1" mb={2}>
                    Take a moment to provide a reason why you are no longer the primary administrator for each of the
                    below organizations:
                  </Typography>
                  <Box mb={2}>
                    <RequiredKey />
                  </Box>
                  <OAAAdminChangeTable organizations={organizationsToDisplay} reasonOptions={reasonOptions} />
                  {errorCount > 0 && (
                    <Grid display="flex" justifyContent="end" mt={1}>
                      <Typography variant="subtitle2" sx={{ color: '#C70002' }}>
                        <WarningTriangleIcon fontSize="xxsmall" />{' '}
                        {`${errorCount} ${errorCount > 1 ? 'errors' : 'error'} found`}
                      </Typography>
                    </Grid>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', marginTop: 3 }}>
                  {showSkipButton ? (
                    <Button color="tertiary" onClick={handleSkip}>
                      I&apos;ll do this later
                    </Button>
                  ) : (
                    <Box />
                  )}
                  <Box>
                    {selectedOAAOrgIds <= 0 && (
                      <>
                        <Button color="secondary" onClick={handleBack}>
                          Back
                        </Button>{' '}
                      </>
                    )}
                    <Button color="primary" type="submit">
                      Next
                    </Button>
                  </Box>
                </CardActions>
              </Paper>
            </form>
          </FormProvider>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
};

OAAAdminChange.propTypes = {
  setSkipBackPage: PropTypes.func.isRequired,
  setCurrentOAAPage: PropTypes.func.isRequired,
  adminChangeFormValues: PropTypes.object,
  setAdminChangeFormValues: PropTypes.func.isRequired,
  reasonOptions: PropTypes.array.isRequired,
};

export default OAAAdminChange;
